import React, { useState, useEffect, useRef, useCallback } from "react";
import storage from "./storage";

// ── Constants & Styles ────────────────────────────────────────────────────────
import {
  InjectCSS,
  CLUB_PASSWORD, ADMIN_PASSWORD, STORAGE_KEY, BOARD_KEY, SHARED,
  border, navy, red, tan, surface, muted, dark, card,
  LOGO_SRC,
} from "./constants";

// ── UI Components ─────────────────────────────────────────────────────────────
import { Btn, Input, SlotSwitch, FancyDropdown } from "./ui";

// ── Member Components ─────────────────────────────────────────────────────────
import {
  migrateMember, EMPTY_MEMBER, EMPTY_CAR,
  deleteFromStorage,
  CarEditor, MemberModal, FaceCard, CarCard,
  Lightbox, MemberDetail, PinPrompt,
} from "./member";

// ── Board Components ──────────────────────────────────────────────────────────
import { BoardView, BoardPostModal } from "./board";

// ── Modals ────────────────────────────────────────────────────────────────────
import { LockScreen, BatchUploadModal } from "./modals";

export default function App() {


  const [isAdmin, setIsAdmin] = useState(null); // null=locked, false=member, true=admin
  const [members, setMembers] = useState([]);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("face");
  const [sortMode, setSortMode] = useState("reg");
  const [makeFilter, setMakeFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [pinPrompt, setPinPrompt] = useState(null);

  // ── Auto-update: reload when a new Vercel deployment is detected ──
  const pendingReload = useRef(false);
  useEffect(() => {
    let currentEtag = null;
    async function checkVersion() {
      try {
        const res = await fetch("/", { method: "HEAD", cache: "no-store" });
        const etag = res.headers.get("etag") || res.headers.get("last-modified");
        if (etag) {
          if (currentEtag && currentEtag !== etag) {
            pendingReload.current = true;
          }
          currentEtag = etag;
        }
      } catch (_) {}
    }
    checkVersion();
    const interval = setInterval(checkVersion, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (!modal && !detail && !pinPrompt && pendingReload.current) {
      pendingReload.current = false;
      window.location.reload();
    }
  }, [modal, detail, pinPrompt]);
  const [posts, setPosts] = useState([]);
  const [boardModal, setBoardModal] = useState(null); // {mode:"new"|"edit"|"reply", post?, parentId?}
  const [boardSort, setBoardSort] = useState("new");       // "new" | "old"
  const [boardTimeMode, setBoardTimeMode] = useState("all"); // "upcoming" | "all"
  const [boardCatFilter, setBoardCatFilter] = useState(""); // "" | "Event" | "Announcement"
  const [boardSearch, setBoardSearch] = useState("");
  const [batchModal, setBatchModal] = useState(null); // "upload" | "download"

  useEffect(() => {
    if (isAdmin === null) return;
    (async () => {
      try {
        const indexRes = await storage.get(`${STORAGE_KEY}-index`, SHARED);
        if (indexRes?.value) {
          const ids = JSON.parse(indexRes.value);
          const loaded = await Promise.all(ids.map(async id => {
            const r = await storage.get(`${STORAGE_KEY}-member-${id}`, SHARED);
            return r?.value ? migrateMember(JSON.parse(r.value)) : null;
          }));
          setMembers(loaded.filter(Boolean));
        } else {
          const res = await storage.get(STORAGE_KEY, SHARED);
          if (res?.value) setMembers(JSON.parse(res.value).map(migrateMember));
        }
      } catch (_) {}
      try {
        const br = await storage.get(BOARD_KEY, SHARED);
        if (br?.value) setPosts(JSON.parse(br.value));
      } catch (_) {}
      setLoading(false);
    })();
  }, [isAdmin]);

  async function persist(list) {
    setSaving(true);
    try {
      for (const m of list) {
        await storage.set(`${STORAGE_KEY}-member-${m.id}`, JSON.stringify(m), SHARED);
      }
      await storage.set(`${STORAGE_KEY}-index`, JSON.stringify(list.map(m => m.id)), SHARED);
    } catch (e) {
      console.error("Save error:", e);
      alert("Save failed — please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function persistBoard(list) {
    try { await storage.set(BOARD_KEY, JSON.stringify(list), SHARED); } catch (_) {}
  }

  function savePost(post) {
    const stamped = { ...post, id: post.id || Date.now().toString(), createdAt: post.createdAt || Date.now(), updatedAt: Date.now() };
    const updated = boardModal.mode === "new" ? [stamped, ...posts] : posts.map(p => p.id === stamped.id ? stamped : p);
    setPosts(updated); persistBoard(updated); setBoardModal(null);
  }

  function saveReply(parentId, text, name) {
    const reply = { id: Date.now().toString(), text, name: name || "", createdAt: Date.now() };
    const updated = posts.map(p => p.id === parentId ? { ...p, replies: [...(p.replies||[]), reply] } : p);
    setPosts(updated); persistBoard(updated); setBoardModal(null);
  }

  function deletePost(id) {
    const post = posts.find(p => p.id === id);
    if (post?.imagePath) { try { deleteFromStorage(post.imagePath); } catch(_) {} }
    const updated = posts.filter(p => p.id !== id);
    setPosts(updated); persistBoard(updated);
  }

  function deleteReply(postId, replyId) {
    const updated = posts.map(p => p.id === postId ? { ...p, replies: (p.replies||[]).filter(r => r.id !== replyId) } : p);
    setPosts(updated); persistBoard(updated);
  }

  async function save(m) {
    const stamped = { ...m, updatedAt: Date.now(), createdAt: m.createdAt || Date.now() };
    const updated = modal.mode === "new"
      ? [...members, stamped]
      : members.map(x => x.id === stamped.id ? stamped : x);
    setMembers(updated);
    await persist(updated);
    setModal(null);
  }

  function del(id) {
    const member = members.find(x => x.id === id);
    if (member) {
      // Delete all car photos from Firebase Storage
      member.cars.forEach(car => {
        car.photos.forEach(p => { if (p.storagePath) deleteFromStorage(p.storagePath); });
      });
    }
    const updated = members.filter(x => x.id !== id);
    setMembers(updated); persist(updated); setModal(null);
    storage.delete(`${STORAGE_KEY}-member-${id}`, SHARED).catch(() => {});
  }
  if (isAdmin === null) return <LockScreen onUnlock={(admin) => setIsAdmin(admin)} logoSrc={LOGO_SRC} />;

  // ── CSV Download ──────────────────────────────────────────────
  function downloadCSV() {
    const headers = ["first_name", "last_name"];
    const rows = members.map(m => [csvEsc(m.firstName), csvEsc(m.lastName || "")].join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "tac-members.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  function csvEsc(v) {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  }

  async function importMembers(newMembers) {
    const updated = [...members, ...newMembers];
    setMembers(updated);
    await persist(updated);
    setBatchModal(null);
  }

  const allMakes = [...new Set(members.flatMap(m => m.cars.map(c => c.make).filter(Boolean)))].sort();

  const filtered = members
    .filter(m => {
      const q = search.toLowerCase();
      const matchSearch = !q || [m.firstName, m.lastName, ...m.cars.flatMap(c => [c.make, c.model, c.year, c.color])]
        .join(" ").toLowerCase().includes(q);
      const matchMake = !makeFilter || m.cars.some(c => c.make === makeFilter);
      return matchSearch && matchMake;
    })
    .slice()
    .sort((a, b) => {
      if (sortMode === "name") {
        const na = (a.firstName || "").toLowerCase();
        const nb = (b.firstName || "").toLowerCase();
        return na.localeCompare(nb, "ja");
      }
      // "reg" = most recently updated first
      return (b.updatedAt ?? 0) - (a.updatedAt ?? 0);
    });

  const totalCars = members.reduce((n, m) => n + m.cars.length, 0);

  const now = Date.now();
  const filteredPosts = posts
    .filter(p => {
      // Category filter
      if (boardCatFilter && p.category !== boardCatFilter) return false;
      // Upcoming: only future events, hide announcements
      if (boardTimeMode === "upcoming") {
        if (p.category === "Announcement") return false;
        if (p.category === "Event" && p.startAt && p.startAt < now) return false;
      }
      // Search
      if (boardSearch) {
        const q = boardSearch.toLowerCase();
        if (!((p.title || "").toLowerCase().includes(q) || (p.body || "").toLowerCase().includes(q))) return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => {
      // Upcoming mode: sort events by startAt ascending (soonest first), then announcements
      if (boardTimeMode === "upcoming") {
        const aIsEvent = a.category === "Event" && a.startAt;
        const bIsEvent = b.category === "Event" && b.startAt;
        if (aIsEvent && bIsEvent) return a.startAt - b.startAt;
        if (aIsEvent) return -1;
        if (bIsEvent) return 1;
      }
      // NEW/OLD sort by post date
      return boardSort === "old"
        ? (a.createdAt ?? 0) - (b.createdAt ?? 0)
        : (b.createdAt ?? 0) - (a.createdAt ?? 0);
    });

  const eventCount = posts.filter(p => p.category === "Event").length;
  const announcementCount = posts.filter(p => p.category === "Announcement").length;

  return (
    <div style={{ minHeight: "100vh", background: dark, color: navy,
      fontFamily: "'DM Mono', monospace" }}>
      <InjectCSS />
      {/* ── HEADER ── */}
      <header style={{ background: navy, position: "sticky", top: 0, zIndex: 50,
        borderBottom: `2px solid ${red}33` }}>
        {/* Single row: logo | title | counts | badge | buttons */}
        <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 5, flexWrap: "nowrap", overflow: "hidden" }}>
          {/* Logo + Title — tap to return to Members */}
          <div onClick={() => setViewMode("face")} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 1, minWidth: 0 }}>
            <img src={LOGO_SRC} alt="TAC" style={{ width: 44, height: 44, borderRadius: "50%",
              objectFit: "cover", border: "2px solid #c8b99a", flexShrink: 0 }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 19,
                color: "#f2ede4", letterSpacing: "0.15em", lineHeight: 1, whiteSpace: "nowrap" }}>TAC Car Club</div>
              {isAdmin && <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                fontFamily: "'DM Mono', monospace", fontWeight: 700, color: `${red}bb`, marginTop: 5 }}>Admin</div>}
            </div>
          </div>
          {/* Spacer */}
          <div style={{ flex: 1 }} />
          {saving && <span style={{ fontSize: 9, color: "#a8b4cc", animation: "pulse 1s infinite", flexShrink: 0 }}>…</span>}
          {/* CSV icon-only buttons + board icon */}
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {isAdmin && (<>
              <button onClick={() => setBatchModal("upload")} title="Import CSV"
                style={{ background: `${navy}cc`, border: `1px solid ${red}55`, borderRadius: 4,
                  cursor: "pointer", padding: "4px 6px", color: "#f2ede4", display: "flex",
                  alignItems: "center", WebkitTapHighlightColor: "transparent" }}>
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1v7M3 5l3-4 3 4" stroke="#f2ede4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1 9v1.5a.5.5 0 00.5.5h9a.5.5 0 00.5-.5V9" stroke="#f2ede4" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
              <button onClick={downloadCSV} title="Export CSV"
                style={{ background: `${navy}cc`, border: `1px solid ${tan}55`, borderRadius: 4,
                  cursor: "pointer", padding: "4px 6px", color: "#f2ede4", display: "flex",
                  alignItems: "center", WebkitTapHighlightColor: "transparent" }}>
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <path d="M6 8V1M3 5l3 4 3-4" stroke="#f2ede4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1 9v1.5a.5.5 0 00.5.5h9a.5.5 0 00.5-.5V9" stroke="#f2ede4" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </>)}
            <button onClick={() => setViewMode(viewMode === "board" ? "face" : "board")}
              style={{ cursor: "pointer", border: "none", background: "none", padding: 0,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                opacity: viewMode === "board" ? 1 : 0.5, transition: "opacity 0.15s" }}>
              <svg width="36" height="34" viewBox="0 0 36 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="7" y1="3" x2="7" y2="31" stroke={viewMode === "board" ? red : "#c8c0b0"} strokeWidth="2.2" strokeLinecap="round"/>
                <rect x="8" y="3" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"}/>
                <rect x="13" y="3" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"} opacity="0.25"/>
                <rect x="18" y="3" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"}/>
                <rect x="23" y="3" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"} opacity="0.25"/>
                <rect x="8" y="8" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"} opacity="0.25"/>
                <rect x="13" y="8" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"}/>
                <rect x="18" y="8" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"} opacity="0.25"/>
                <rect x="23" y="8" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"}/>
                <rect x="8" y="13" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"}/>
                <rect x="13" y="13" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"} opacity="0.25"/>
                <rect x="18" y="13" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"}/>
                <rect x="23" y="13" width="5" height="5" fill={viewMode === "board" ? red : "#c8c0b0"} opacity="0.25"/>
              </svg>

            </button>
          </div>
          {/* ADD MEMBER - fuel pump */}
          <button onClick={() => setModal({ mode: "new" })}
            style={{
              width: 58, height: 58, borderRadius: "50%", flexShrink: 0,
              background: "radial-gradient(circle at 40% 35%, #2e3a55, #1a2540)",
              border: "2px solid #ffffff22",
              boxShadow: "0 0 0 2px #ffffff10, inset 0 2px 4px rgba(255,255,255,0.06)",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              WebkitTapHighlightColor: "transparent", transition: "all 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 0 2px #ffffff35, inset 0 2px 4px rgba(255,255,255,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 0 2px #ffffff10, inset 0 2px 4px rgba(255,255,255,0.06)"; }}
            onMouseDown={e => { e.currentTarget.style.transform = "scale(0.94)"; }}
            onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
            title="Add New Member">
            <svg width="28" height="28" viewBox="308 45 105 108" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="317" y="50" width="52" height="90" rx="4" fill="#f5c842"/>
              <rect x="312" y="136" width="62" height="8" rx="3" fill="#f5c842"/>
              <rect x="326" y="60" width="34" height="18" rx="2" fill="rgba(26,37,64,0.7)"/>
              <path d="M369 88 C376 88 379 94 379 102 C379 112 379 116 379 118 C379 128 386 132 393 132 C398 132 401 128 400 120 L399 82"
                stroke="#f5c842" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M399 76 C400 70 398 63 392 59"
                stroke="#f5c842" strokeWidth="10" strokeLinecap="round" fill="none"/>
              <path d="M392 59 C388 57 384 57 381 55"
                stroke="#f5c842" strokeWidth="7" strokeLinecap="round" fill="none"/>
              <path d="M381 55 C380 54 379 54 379 54"
                stroke="#f5c842" strokeWidth="3" strokeLinecap="round" fill="none"/>
            </svg>
          </button>
          {/* STOP */}
          <button onClick={() => setIsAdmin(null)}
            style={{
              width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
              background: `radial-gradient(circle at 40% 35%, #2e3a55, #1a2540)`,
              border: "2px solid #ffffff22",
              boxShadow: "0 0 0 2px #ffffff10",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              WebkitTapHighlightColor: "transparent", transition: "all 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 0 2px #ffffff30"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 0 2px #ffffff10"; }}
            title="Sign Out">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.1em", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", userSelect: "none", fontWeight: 700 }}>STOP</span>
          </button>
        </div>
      </header>
      {/* ── TOOLBAR ── */}
      <div style={{ background: surface, borderBottom: `1px solid ${border}`, padding: "8px 12px",
        display: "flex", alignItems: "center", gap: 10 }}>
        {viewMode === "board" ? (<>
          {/* BOARD TOOLBAR */}
          {/* Left: switches */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, flexShrink: 0 }}>
            <SlotSwitch
              options={[{value:"new",label:"New"},{value:"old",label:"Old"}]}
              value={boardSort}
              onChange={setBoardSort}
            />
            <SlotSwitch
              options={[{value:"all",label:"All"},{value:"upcoming",label:"Upcoming"}]}
              value={boardTimeMode}
              onChange={setBoardTimeMode}
            />
          </div>
          {/* Divider */}
          <div style={{ width: 1, alignSelf: "stretch", background: `${navy}20`, flexShrink: 0 }} />
          {/* Event / Announcement counts */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0, justifyContent: "center" }}>
            {[["Events", eventCount], ["News", announcementCount]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: red, lineHeight: 1 }}>{v}</span>
                <span style={{ fontSize: 9, color: muted, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>{k}</span>
              </div>
            ))}
          </div>
          {/* Divider */}
          <div style={{ width: 1, alignSelf: "stretch", background: `${navy}20`, flexShrink: 0 }} />
          {/* Category filter + search */}
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center", gap: 8 }}>
            <FancyDropdown
              value={boardCatFilter}
              onChange={setBoardCatFilter}
              placeholder="All Posts"
              options={[
                { value: "Event", label: "Events" },
                { value: "Announcement", label: "Announcements" },
              ]}
            />
            <div style={{ position: "relative" }}>
              <Input placeholder="Search posts…" value={boardSearch}
                onChange={e => setBoardSearch(e.target.value)}
                style={{ padding: "7px 10px", fontSize: 12, width: "100%", boxSizing: "border-box",
                  border: `1px solid ${boardSearch ? red : border}` }} />
              {boardSearch && (
                <button onClick={() => setBoardSearch("")}
                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: muted,
                    fontSize: 14, padding: 2, lineHeight: 1 }}>✕</button>
              )}
            </div>
          </div>
        </>) : (<>
          {/* MEMBER TOOLBAR */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, flexShrink: 0 }}>
            <SlotSwitch
              options={[{value:"face",label:"Members"},{value:"car",label:"Garage"}]}
              value={viewMode}
              onChange={setViewMode}
            />
            <SlotSwitch
              options={[{value:"reg",label:"Updated"},{value:"name",label:"A–Z"}]}
              value={sortMode}
              onChange={setSortMode}
            />
          </div>
          <div style={{ width: 1, alignSelf: "stretch", background: `${navy}20`, flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0, justifyContent: "center", minWidth: 60 }}>
            {[["Members", members.length], ["Cars", totalCars]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: red, lineHeight: 1 }}>{v}</span>
                <span style={{ fontSize: 9, color: muted, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>{k}</span>
              </div>
            ))}
          </div>
          <div style={{ width: 1, alignSelf: "stretch", background: `${navy}20`, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center", gap: 8 }}>
            <FancyDropdown
              value={makeFilter}
              onChange={setMakeFilter}
              placeholder="All Makes"
              options={allMakes.map(m => ({ value: m, label: m }))}
            />
            <div style={{ position: "relative" }}>
              <Input placeholder="Search…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ padding: "7px 10px", fontSize: 12, width: "100%", boxSizing: "border-box",
                  border: `1px solid ${search ? red : border}` }} />
              {search && (
                <button onClick={() => setSearch("")}
                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: muted,
                    fontSize: 14, padding: 2, lineHeight: 1 }}>✕</button>
              )}
            </div>
          </div>
        </>)}
      </div>

      <main style={{ padding: "20px 16px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
            <div style={{ width: 26, height: 26, border: `2px solid ${border}`,
              borderTop: `2px solid ${red}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          </div>
        ) : viewMode === "board" ? (
          <BoardView
            posts={filteredPosts}
            isAdmin={isAdmin}
            onNewPost={() => setBoardModal({ mode: "new" })}
            onEditPost={p => setBoardModal({ mode: "edit", post: p })}
            onDeletePost={deletePost}
            onReply={parentId => setBoardModal({ mode: "reply", parentId })}
            onDeleteReply={deleteReply}
          />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: muted }}>
            {members.length === 0 ? (
              <>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40,
                  color: `${navy}20`, letterSpacing: "0.2em", marginBottom: 12 }}>START YOUR ROSTER</div>
                <div style={{ fontSize: 14, marginBottom: 20 }}>No members yet.</div>
                <Btn onClick={() => setModal({ mode: "new" })}>+ Add First Member</Btn>
              </>
            ) : (
              <>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32,
                  color: `${navy}25`, letterSpacing: "0.2em", marginBottom: 8 }}>WE ARE LOW ON FUEL</div>
                <div style={{ fontSize: 12, color: muted, fontFamily: "'DM Mono', monospace",
                  letterSpacing: "0.1em" }}>No results found</div>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: "grid",
            gridTemplateColumns: viewMode === "face"
              ? "repeat(auto-fill, minmax(110px, 1fr))"
              : "repeat(auto-fill, minmax(160px, 1fr))",
            gap: viewMode === "face" ? 10 : 12 }}>
            {viewMode === "face"
              ? filtered.map((m, i) => <FaceCard key={m.id} member={m} idx={i} memberNum={members.indexOf(m) + 1} onDetail={setDetail} />)
              : filtered.flatMap((m, i) => {
                  const carsToShow = makeFilter
                    ? m.cars.map((car, ci) => ({ car, ci })).filter(({ car }) => car.make === makeFilter)
                    : (m.cars.length > 0 ? m.cars.map((car, ci) => ({ car, ci })) : [{ car: null, ci: 0 }]);
                  return carsToShow.map(({ ci }, j) => (
                    <CarCard key={`${m.id}-${ci}`} member={m} carIdx={ci} idx={i * 3 + j} memberNum={members.indexOf(m) + 1} onDetail={setDetail} />
                  ));
                })
            }
          </div>
        )}
      </main>

      {modal && (
        <MemberModal
          member={modal.mode === "edit" ? modal.member : null}
          isAdmin={isAdmin}
          onSave={save}
          onCancel={() => setModal(null)}
          onDelete={modal.mode === "edit" && isAdmin ? () => del(modal.member.id) : null}
          saving={saving}
        />
      )}
      {detail && !modal && (
        <MemberDetail
          member={detail}
          isAdmin={isAdmin}
          onEdit={() => {
            if (isAdmin) {
              setModal({ mode: "edit", member: detail }); setDetail(null);
            } else if (detail.pin) {
              setPinPrompt(detail);
            } else {
              setModal({ mode: "edit", member: detail }); setDetail(null);
            }
          }}
          onClose={() => setDetail(null)}
        />
      )}
      {pinPrompt && (
        <PinPrompt
          member={pinPrompt}
          onVerified={() => { setModal({ mode: "edit", member: pinPrompt }); setDetail(null); setPinPrompt(null); }}
          onSetPin={newPin => {
            // Save PIN then open edit
            const updated = members.map(x => x.id === pinPrompt.id ? { ...x, pin: newPin } : x);
            setMembers(updated); persist(updated);
            const withPin = { ...pinPrompt, pin: newPin };
            setModal({ mode: "edit", member: withPin }); setDetail(null); setPinPrompt(null);
          }}
          onClose={() => setPinPrompt(null)}
        />
      )}
      {boardModal && (
        <BoardPostModal
          mode={boardModal.mode}
          post={boardModal.post}
          parentId={boardModal.parentId}
          posts={posts}
          onSavePost={savePost}
          onSaveReply={saveReply}
          onCancel={() => setBoardModal(null)}
          onDeletePost={deletePost}
        />
      )}
      {batchModal === "upload" && (
        <BatchUploadModal
          existingCount={members.length}
          onImport={importMembers}
          onCancel={() => setBatchModal(null)}
        />
      )}
    </div>
  );
}
